import { useState, useEffect, createContext, useContext, useCallback, createElement, useMemo, useRef, useReducer, forwardRef, Fragment } from 'react';
import { u as useIsomorphicLayoutEffect, a as useForceUpdate, b as useComposedRefs, g as getOwnerDocument, i as isFunction, c as isString, d as Popover, e as PropTypes, r as reactIs, w as warning } from './reach-popover.esm-c19739dd.js';

/*
 * Welcome to @reach/auto-id!

 * Let's see if we can make sense of why this hook exists and its
 * implementation.
 *
 * Some background:
 *   1. Accessibility APIs rely heavily on element IDs
 *   2. Requiring developers to put IDs on every element in Reach UI is both
 *      cumbersome and error-prone
 *   3. With a component model, we can generate IDs for them!
 *
 * Solution 1: Generate random IDs.
 *
 * This works great as long as you don't server render your app. When React (in
 * the client) tries to reuse the markup from the server, the IDs won't match
 * and React will then recreate the entire DOM tree.
 *
 * Solution 2: Increment an integer
 *
 * This sounds great. Since we're rendering the exact same tree on the server
 * and client, we can increment a counter and get a deterministic result between
 * client and server. Also, JS integers can go up to nine-quadrillion. I'm
 * pretty sure the tab will be closed before an app never needs
 * 10 quadrillion IDs!
 *
 * Problem solved, right?
 *
 * Ah, but there's a catch! React's concurrent rendering makes this approach
 * non-deterministic. While the client and server will end up with the same
 * elements in the end, depending on suspense boundaries (and possibly some user
 * input during the initial render) the incrementing integers won't always match
 * up.
 *
 * Solution 3: Don't use IDs at all on the server; patch after first render.
 *
 * What we've done here is solution 2 with some tricks. With this approach, the
 * ID returned is an empty string on the first render. This way the server and
 * client have the same markup no matter how wild the concurrent rendering may
 * have gotten.
 *
 * After the render, we patch up the components with an incremented ID. This
 * causes a double render on any components with `useId`. Shouldn't be a problem
 * since the components using this hook should be small, and we're only updating
 * the ID attribute on the DOM, nothing big is happening.
 *
 * It doesn't have to be an incremented number, though--we could do generate
 * random strings instead, but incrementing a number is probably the cheapest
 * thing we can do.
 *
 * Additionally, we only do this patchup on the very first client render ever.
 * Any calls to `useId` that happen dynamically in the client will be
 * populated immediately with a value. So, we only get the double render after
 * server hydration and never again, SO BACK OFF ALRIGHT?
 */
var serverHandoffComplete = false;
var id = 0;
var genId = function genId() {
  return ++id;
};
/**
 * useId
 *
 * Autogenerate IDs to facilitate WAI-ARIA and server rendering.
 *
 * Note: The returned ID will initially be `null` and will update after a
 * component mounts. Users may need to supply their own ID if they need
 * consistent values for SSR.
 *
 * @see Docs https://reach.tech/auto-id
 */

function useId(idFromProps) {
  /*
   * If this instance isn't part of the initial render, we don't have to do the
   * double render/patch-up dance. We can just generate the ID and return it.
   */
  var initialId = idFromProps || (serverHandoffComplete ? genId() : null);
  var _React$useState = useState(initialId),
    id = _React$useState[0],
    setId = _React$useState[1];
  useIsomorphicLayoutEffect(function () {
    if (id === null) {
      /*
       * Patch the ID after render. We do this in `useLayoutEffect` to avoid any
       * rendering flicker, though it'll make the first render slower (unlikely
       * to matter, but you're welcome to measure your app and let us know if
       * it's a problem).
       */
      setId(genId());
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function () {
    if (serverHandoffComplete === false) {
      /*
       * Flag all future uses of `useId` to skip the update dance. This is in
       * `useEffect` because it goes after `useLayoutEffect`, ensuring we don't
       * accidentally bail out of the patch-up dance prematurely.
       */
      serverHandoffComplete = true;
    }
  }, []);
  return id != null ? String(id) : undefined;
}

function createNamedContext(name, defaultValue) {
  var Ctx = /*#__PURE__*/createContext(defaultValue);
  if (process.env.NODE_ENV !== "production") {
    Ctx.displayName = name;
  }
  return Ctx;
}

function noop() {}

function _objectWithoutPropertiesLoose$2(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _extends$2() {
  _extends$2 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$2.apply(this, arguments);
}
var _excluded$2 = ["element", "index"];
function createDescendantContext(name, initialValue) {
  if (initialValue === void 0) {
    initialValue = {};
  }
  var descendants = [];
  return createNamedContext(name, _extends$2({
    descendants: descendants,
    registerDescendant: noop,
    unregisterDescendant: noop
  }, initialValue));
}
/**
 * This hook registers our descendant by passing it into an array. We can then
 * search that array by to find its index when registering it in the component.
 * We use this for focus management, keyboard navigation, and typeahead
 * functionality for some components.
 *
 * The hook accepts the element node and (optionally) a key. The key is useful
 * if multiple descendants have identical text values and we need to
 * differentiate siblings for some reason.
 *
 * Our main goals with this are:
 *   1) maximum composability,
 *   2) minimal API friction
 *   3) SSR compatibility*
 *   4) concurrent safe
 *   5) index always up-to-date with the tree despite changes
 *   6) works with memoization of any component in the tree (hopefully)
 *
 * As for SSR, the good news is that we don't actually need the index on the
 * server for most use-cases, as we are only using it to determine the order of
 * composed descendants for keyboard navigation. However, in the few cases where
 * this is not the case, we can require an explicit index from the app.
 */

function useDescendant(descendant, context, indexProp) {
  var forceUpdate = useForceUpdate();
  var _React$useContext = useContext(context),
    registerDescendant = _React$useContext.registerDescendant,
    unregisterDescendant = _React$useContext.unregisterDescendant,
    descendants = _React$useContext.descendants; // This will initially return -1 because we haven't registered the descendant
  // on the first render. After we register, this will then return the correct
  // index on the following render and we will re-register descendants so that
  // everything is up-to-date before the user interacts with a collection.

  var index = indexProp != null ? indexProp : descendants.findIndex(function (item) {
    return item.element === descendant.element;
  }); // Prevent any flashing

  useIsomorphicLayoutEffect(function () {
    if (!descendant.element) forceUpdate();
    registerDescendant(_extends$2({}, descendant, {
      index: index
    }));
    return function () {
      unregisterDescendant(descendant.element);
    };
  }, [descendant, forceUpdate, index, registerDescendant, unregisterDescendant].concat(Object.values(descendant)));
  return index;
}
function useDescendantsInit() {
  return useState([]);
}
function useDescendants(ctx) {
  return useContext(ctx).descendants;
}
function DescendantProvider(_ref) {
  var Ctx = _ref.context,
    children = _ref.children,
    items = _ref.items,
    set = _ref.set;
  var registerDescendant = useCallback(function (_ref2) {
    var element = _ref2.element,
      explicitIndex = _ref2.index,
      rest = _objectWithoutPropertiesLoose$2(_ref2, _excluded$2);
    if (!element) {
      return;
    }
    set(function (items) {
      var newItems;
      if (explicitIndex != null) {
        newItems = [].concat(items, [_extends$2({}, rest, {
          element: element,
          index: explicitIndex
        })]);
      } else if (items.length === 0) {
        // If there are no items, register at index 0 and bail.
        newItems = [].concat(items, [_extends$2({}, rest, {
          element: element,
          index: 0
        })]);
      } else if (items.find(function (item) {
        return item.element === element;
      })) {
        // If the element is already registered, just use the same array
        newItems = items;
      } else {
        // When registering a descendant, we need to make sure we insert in
        // into the array in the same order that it appears in the DOM. So as
        // new descendants are added or maybe some are removed, we always know
        // that the array is up-to-date and correct.
        //
        // So here we look at our registered descendants and see if the new
        // element we are adding appears earlier than an existing descendant's
        // DOM node via `node.compareDocumentPosition`. If it does, we insert
        // the new element at this index. Because `registerDescendant` will be
        // called in an effect every time the descendants state value changes,
        // we should be sure that this index is accurate when descendent
        // elements come or go from our component.
        var index = items.findIndex(function (item) {
          if (!item.element || !element) {
            return false;
          } // Does this element's DOM node appear before another item in the
          // array in our DOM tree? If so, return true to grab the index at
          // this point in the array so we know where to insert the new
          // element.

          return Boolean(item.element.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING);
        });
        var newItem = _extends$2({}, rest, {
          element: element,
          index: index
        }); // If an index is not found we will push the element to the end.

        if (index === -1) {
          newItems = [].concat(items, [newItem]);
        } else {
          newItems = [].concat(items.slice(0, index), [newItem], items.slice(index));
        }
      }
      return newItems.map(function (item, index) {
        return _extends$2({}, item, {
          index: index
        });
      });
    });
  },
  // set is a state setter initialized by the useDescendantsInit hook.
  // We can safely ignore the lint warning here because it will not change
  // between renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  var unregisterDescendant = useCallback(function (element) {
    if (!element) {
      return;
    }
    set(function (items) {
      return items.filter(function (item) {
        return element !== item.element;
      });
    });
  },
  // set is a state setter initialized by the useDescendantsInit hook.
  // We can safely ignore the lint warning here because it will not change
  // between renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return /*#__PURE__*/createElement(Ctx.Provider, {
    value: useMemo(function () {
      return {
        descendants: items,
        registerDescendant: registerDescendant,
        unregisterDescendant: unregisterDescendant
      };
    }, [items, registerDescendant, unregisterDescendant])
  }, children);
}
/**
 * Testing this as an abstraction for compound components that use keyboard
 * navigation. Hoping this will help us prevent bugs and mismatched behavior
 * across various components, but it may also prove to be too messy of an
 * abstraction in the end.
 *
 * Currently used in:
 *   - Tabs
 *   - Accordion
 *
 * @param context
 * @param options
 */

function useDescendantKeyDown(context, options) {
  var _React$useContext2 = useContext(context),
    descendants = _React$useContext2.descendants;
  var callback = options.callback,
    currentIndex = options.currentIndex,
    filter = options.filter,
    _options$key = options.key,
    key = _options$key === void 0 ? "index" : _options$key,
    _options$orientation = options.orientation,
    orientation = _options$orientation === void 0 ? "vertical" : _options$orientation,
    _options$rotate = options.rotate,
    rotate = _options$rotate === void 0 ? true : _options$rotate,
    _options$rtl = options.rtl,
    rtl = _options$rtl === void 0 ? false : _options$rtl;
  return function handleKeyDown(event) {
    if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "PageUp", "PageDown", "Home", "End"].includes(event.key)) {
      return;
    }
    var index = currentIndex != null ? currentIndex : -1; // If we use a filter function, we need to re-index our descendants array
    // so that filtered descendent elements aren't selected.

    var selectableDescendants = filter ? descendants.filter(filter) : descendants; // Current index should map to the updated array vs. the original
    // descendants array.

    if (filter) {
      index = selectableDescendants.findIndex(function (descendant) {
        return descendant.index === currentIndex;
      });
    } // We need some options for any of this to work!

    if (!selectableDescendants.length) {
      return;
    }
    function getNextOption() {
      var atBottom = index === selectableDescendants.length - 1;
      return atBottom ? rotate ? getFirstOption() : selectableDescendants[index] : selectableDescendants[(index + 1) % selectableDescendants.length];
    }
    function getPreviousOption() {
      var atTop = index === 0;
      return atTop ? rotate ? getLastOption() : selectableDescendants[index] : selectableDescendants[(index - 1 + selectableDescendants.length) % selectableDescendants.length];
    }
    function getFirstOption() {
      return selectableDescendants[0];
    }
    function getLastOption() {
      return selectableDescendants[selectableDescendants.length - 1];
    }
    switch (event.key) {
      case "ArrowDown":
        if (orientation === "vertical" || orientation === "both") {
          event.preventDefault();
          var next = getNextOption();
          callback(key === "option" ? next : next[key]);
        }
        break;
      case "ArrowUp":
        if (orientation === "vertical" || orientation === "both") {
          event.preventDefault();
          var prev = getPreviousOption();
          callback(key === "option" ? prev : prev[key]);
        }
        break;
      case "ArrowLeft":
        if (orientation === "horizontal" || orientation === "both") {
          event.preventDefault();
          var nextOrPrev = (rtl ? getNextOption : getPreviousOption)();
          callback(key === "option" ? nextOrPrev : nextOrPrev[key]);
        }
        break;
      case "ArrowRight":
        if (orientation === "horizontal" || orientation === "both") {
          event.preventDefault();
          var prevOrNext = (rtl ? getPreviousOption : getNextOption)();
          callback(key === "option" ? prevOrNext : prevOrNext[key]);
        }
        break;
      case "PageUp":
        event.preventDefault();
        var prevOrFirst = (event.ctrlKey ? getPreviousOption : getFirstOption)();
        callback(key === "option" ? prevOrFirst : prevOrFirst[key]);
        break;
      case "Home":
        event.preventDefault();
        var first = getFirstOption();
        callback(key === "option" ? first : first[key]);
        break;
      case "PageDown":
        event.preventDefault();
        var nextOrLast = (event.ctrlKey ? getNextOption : getLastOption)();
        callback(key === "option" ? nextOrLast : nextOrLast[key]);
        break;
      case "End":
        event.preventDefault();
        var last = getLastOption();
        callback(key === "option" ? last : last[key]);
        break;
    }
  };
} ////////////////////////////////////////////////////////////////////////////////

/**
 * Detects right clicks
 *
 * @param nativeEvent
 */
function isRightClick(nativeEvent) {
  return "which" in nativeEvent ? nativeEvent.which === 3 : "button" in nativeEvent ? nativeEvent.button === 2 : false;
}

/**
 * Returns the previous value of a reference after a component update.
 *
 * @param value
 */

function usePrevious(value) {
  var ref = useRef(null);
  useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref.current;
}

/**
 * Joins strings to format IDs for compound components.
 *
 * @param args
 */
function makeId() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  return args.filter(function (val) {
    return val != null;
  }).join("--");
}

function useStatefulRefValue(ref, initialState) {
  var _useState = useState(initialState),
    state = _useState[0],
    setState = _useState[1];
  var callbackRef = useCallback(function (refValue) {
    ref.current = refValue;
    setState(refValue); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [state, callbackRef];
}

/**
 * Wraps a lib-defined event handler and a user-defined event handler, returning
 * a single handler that allows a user to prevent lib-defined handlers from
 * firing.
 *
 * @param theirHandler User-supplied event handler
 * @param ourHandler Library-supplied event handler
 */
function composeEventHandlers(theirHandler, ourHandler) {
  return function (event) {
    theirHandler && theirHandler(event);
    if (!event.defaultPrevented) {
      return ourHandler(event);
    }
  };
}

function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1.apply(this, arguments);
}
var _excluded$1 = ["onKeyDown", "onMouseDown", "id", "ref"],
  _excluded2$1 = ["as"],
  _excluded3$1 = ["index", "isLink", "onClick", "onDragStart", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseMove", "onMouseUp", "onSelect", "disabled", "onFocus", "valueText", "ref"],
  _excluded4$1 = ["as"],
  _excluded5$1 = ["id", "onKeyDown", "ref"],
  _excluded6$1 = ["as"],
  _excluded7$1 = ["onBlur", "portal", "position", "ref"],
  _excluded8$1 = ["as"];
////////////////////////////////////////////////////////////////////////////////
// Actions
var CLEAR_SELECTION_INDEX = "CLEAR_SELECTION_INDEX";
var CLICK_MENU_ITEM = "CLICK_MENU_ITEM";
var CLOSE_MENU = "CLOSE_MENU";
var OPEN_MENU_AT_FIRST_ITEM = "OPEN_MENU_AT_FIRST_ITEM";
var OPEN_MENU_AT_INDEX = "OPEN_MENU_AT_INDEX";
var OPEN_MENU_CLEARED = "OPEN_MENU_CLEARED";
var SEARCH_FOR_ITEM = "SEARCH_FOR_ITEM";
var SELECT_ITEM_AT_INDEX = "SELECT_ITEM_AT_INDEX";
var SET_BUTTON_ID = "SET_BUTTON_ID";
var DropdownDescendantContext = /*#__PURE__*/createDescendantContext("DropdownDescendantContext");
var DropdownContext = /*#__PURE__*/createNamedContext("DropdownContext", {});
var initialState = {
  // The button ID is needed for aria controls and can be set directly and
  // updated for top-level use via context. Otherwise a default is set by useId.
  // TODO: Consider deprecating direct ID in 1.0 in favor of id at the top level
  //       for passing deterministic IDs to descendent components.
  triggerId: null,
  // Whether or not the dropdown is expanded
  isExpanded: false,
  // When a user begins typing a character string, the selection will change if
  // a matching item is found
  typeaheadQuery: "",
  // The index of the current selected item. When the selection is cleared a
  // value of -1 is used.
  selectionIndex: -1
}; ////////////////////////////////////////////////////////////////////////////////
// Dropdown!

var DropdownProvider = function DropdownProvider(_ref) {
  var id = _ref.id,
    children = _ref.children;
  var triggerRef = useRef(null);
  var dropdownRef = useRef(null);
  var popoverRef = useRef(null);
  var _useDescendantsInit = useDescendantsInit(),
    descendants = _useDescendantsInit[0],
    setDescendants = _useDescendantsInit[1];
  var _id = useId(id);
  var dropdownId = id || makeId("menu", _id);
  var triggerId = makeId("menu-button", dropdownId);
  var _React$useReducer = useReducer(reducer, _extends$1({}, initialState, {
      triggerId: triggerId
    })),
    state = _React$useReducer[0],
    dispatch = _React$useReducer[1]; // We use an event listener attached to the window to capture outside clicks
  // that close the dropdown. We don't want the initial button click to trigger
  // this when a dropdown is closed, so we can track this behavior in a ref for
  // now. We shouldn't need this when we rewrite with state machine logic.

  var triggerClickedRef = useRef(false); // We will put children callbacks in a ref to avoid triggering endless render
  // loops when using render props if the app code doesn't useCallback
  // https://github.com/reach/reach-ui/issues/523

  var selectCallbacks = useRef([]); // If the popover's position overlaps with an option when the popover
  // initially opens, the mouseup event will trigger a select. To prevent that,
  // we decide the control is only ready to make a selection if the pointer
  // moves a certain distance OR if the mouse button is pressed for a certain
  // length of time, otherwise the user is just registering the initial button
  // click rather than selecting an item.
  // For context on some implementation details, see https://github.com/reach/reach-ui/issues/563

  var readyToSelect = useRef(false);
  var mouseDownStartPosRef = useRef({
    x: 0,
    y: 0
  }); // Trying a new approach for splitting up contexts by stable/unstable
  // references. We'll see how it goes!

  var context = {
    dispatch: dispatch,
    dropdownId: dropdownId,
    dropdownRef: dropdownRef,
    mouseDownStartPosRef: mouseDownStartPosRef,
    popoverRef: popoverRef,
    readyToSelect: readyToSelect,
    selectCallbacks: selectCallbacks,
    state: state,
    triggerClickedRef: triggerClickedRef,
    triggerRef: triggerRef
  }; // When the dropdown is open, focus is placed on the dropdown itself so that
  // keyboard navigation is still possible.

  useEffect(function () {
    if (state.isExpanded) {
      // @ts-ignore
      window.__REACH_DISABLE_TOOLTIPS = true;
      window.requestAnimationFrame(function () {
        focus(dropdownRef.current);
      });
    } else {
      // We want to ignore the immediate focus of a tooltip so it doesn't pop up
      // again when the dropdown closes, only pops up when focus returns again
      // to the tooltip (like native OS tooltips).
      // @ts-ignore
      window.__REACH_DISABLE_TOOLTIPS = false;
    }
  }, [state.isExpanded]);
  return /*#__PURE__*/createElement(DescendantProvider, {
    context: DropdownDescendantContext,
    items: descendants,
    set: setDescendants
  }, /*#__PURE__*/createElement(DropdownContext.Provider, {
    value: context
  }, isFunction(children) ? children({
    isExpanded: state.isExpanded,
    // TODO: Remove in 1.0
    isOpen: state.isExpanded
  }) : children));
};
if (process.env.NODE_ENV !== "production") {
  DropdownProvider.displayName = "DropdownProvider";
} ////////////////////////////////////////////////////////////////////////////////

function useDropdownTrigger(_ref2) {
  var onKeyDown = _ref2.onKeyDown,
    onMouseDown = _ref2.onMouseDown,
    id = _ref2.id,
    forwardedRef = _ref2.ref,
    props = _objectWithoutPropertiesLoose$1(_ref2, _excluded$1);
  var _useDropdownContext = useDropdownContext(),
    dispatch = _useDropdownContext.dispatch,
    dropdownId = _useDropdownContext.dropdownId,
    mouseDownStartPosRef = _useDropdownContext.mouseDownStartPosRef,
    triggerClickedRef = _useDropdownContext.triggerClickedRef,
    triggerRef = _useDropdownContext.triggerRef,
    _useDropdownContext$s = _useDropdownContext.state,
    triggerId = _useDropdownContext$s.triggerId,
    isExpanded = _useDropdownContext$s.isExpanded;
  var ref = useComposedRefs(triggerRef, forwardedRef);
  var items = useDropdownDescendants();
  var firstNonDisabledIndex = useMemo(function () {
    return items.findIndex(function (item) {
      return !item.disabled;
    });
  }, [items]);
  useEffect(function () {
    if (id != null && id !== triggerId) {
      dispatch({
        type: SET_BUTTON_ID,
        payload: id
      });
    }
  }, [triggerId, dispatch, id]);
  useEffect(function () {
    if (id == null) {
      dispatch({
        type: SET_BUTTON_ID,
        payload: makeId("menu-button", dropdownId)
      });
    }
  }, [dropdownId, dispatch, id]);
  function handleKeyDown(event) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault(); // prevent scroll

        dispatch({
          type: OPEN_MENU_AT_INDEX,
          payload: {
            index: firstNonDisabledIndex
          }
        });
        break;
      case "Enter":
      case " ":
        dispatch({
          type: OPEN_MENU_AT_INDEX,
          payload: {
            index: firstNonDisabledIndex
          }
        });
        break;
    }
  }
  function handleMouseDown(event) {
    if (isRightClick(event.nativeEvent)) {
      return;
    }
    mouseDownStartPosRef.current = {
      x: event.clientX,
      y: event.clientY
    };
    if (!isExpanded) {
      triggerClickedRef.current = true;
    }
    if (isExpanded) {
      dispatch({
        type: CLOSE_MENU
      });
    } else {
      dispatch({
        type: OPEN_MENU_CLEARED
      });
    }
  }
  return {
    data: {
      isExpanded: isExpanded,
      controls: dropdownId
    },
    props: _extends$1({}, props, {
      ref: ref,
      id: triggerId || undefined,
      onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown),
      onMouseDown: composeEventHandlers(onMouseDown, handleMouseDown),
      type: "button"
    })
  };
}
var DropdownTrigger = /*#__PURE__*/forwardRef(function (_ref3, forwardedRef) {
  var _ref3$as = _ref3.as,
    Comp = _ref3$as === void 0 ? "button" : _ref3$as,
    rest = _objectWithoutPropertiesLoose$1(_ref3, _excluded2$1);
  var _useDropdownTrigger = useDropdownTrigger(_extends$1({}, rest, {
      ref: forwardedRef
    })),
    props = _useDropdownTrigger.props;
  return /*#__PURE__*/createElement(Comp, _extends$1({
    "data-reach-dropdown-trigger": ""
  }, props));
});
if (process.env.NODE_ENV !== "production") {
  DropdownTrigger.displayName = "DropdownTrigger";
} ////////////////////////////////////////////////////////////////////////////////

function useDropdownItem(_ref4) {
  var indexProp = _ref4.index,
    _ref4$isLink = _ref4.isLink,
    isLink = _ref4$isLink === void 0 ? false : _ref4$isLink,
    onClick = _ref4.onClick,
    onDragStart = _ref4.onDragStart,
    onMouseDown = _ref4.onMouseDown,
    onMouseEnter = _ref4.onMouseEnter,
    onMouseLeave = _ref4.onMouseLeave,
    onMouseMove = _ref4.onMouseMove,
    onMouseUp = _ref4.onMouseUp,
    onSelect = _ref4.onSelect,
    disabled = _ref4.disabled,
    onFocus = _ref4.onFocus,
    valueTextProp = _ref4.valueText,
    forwardedRef = _ref4.ref,
    props = _objectWithoutPropertiesLoose$1(_ref4, _excluded3$1);
  var _useDropdownContext2 = useDropdownContext(),
    dispatch = _useDropdownContext2.dispatch,
    dropdownRef = _useDropdownContext2.dropdownRef,
    mouseDownStartPosRef = _useDropdownContext2.mouseDownStartPosRef,
    readyToSelect = _useDropdownContext2.readyToSelect,
    selectCallbacks = _useDropdownContext2.selectCallbacks,
    triggerRef = _useDropdownContext2.triggerRef,
    _useDropdownContext2$ = _useDropdownContext2.state,
    selectionIndex = _useDropdownContext2$.selectionIndex,
    isExpanded = _useDropdownContext2$.isExpanded;
  var ownRef = useRef(null); // After the ref is mounted to the DOM node, we check to see if we have an
  // explicit valueText prop before looking for the node's textContent for
  // typeahead functionality.

  var _React$useState = useState(valueTextProp || ""),
    valueText = _React$useState[0],
    setValueText = _React$useState[1];
  var setValueTextFromDOM = useCallback(function (node) {
    if (!valueTextProp && node != null && node.textContent) {
      setValueText(node.textContent);
    }
  }, [valueTextProp]);
  var mouseEventStarted = useRef(false);
  var _useStatefulRefValue = useStatefulRefValue(ownRef, null),
    element = _useStatefulRefValue[0],
    handleRefSet = _useStatefulRefValue[1];
  var descendant = useMemo(function () {
    return {
      element: element,
      key: valueText,
      disabled: disabled,
      isLink: isLink
    };
  }, [disabled, element, isLink, valueText]);
  var index = useDescendant(descendant, DropdownDescendantContext, indexProp);
  var isSelected = index === selectionIndex && !disabled;
  var ref = useComposedRefs(forwardedRef, handleRefSet, setValueTextFromDOM); // Update the callback ref array on every render

  selectCallbacks.current[index] = onSelect;
  function select() {
    focus(triggerRef.current);
    onSelect && onSelect();
    dispatch({
      type: CLICK_MENU_ITEM
    });
  }
  function handleClick(event) {
    if (isRightClick(event.nativeEvent)) {
      return;
    }
    if (isLink) {
      if (disabled) {
        event.preventDefault();
      } else {
        select();
      }
    }
  }
  function handleDragStart(event) {
    // Because we don't preventDefault on mousedown for links (we need the
    // native click event), clicking and holding on a link triggers a
    // dragstart which we don't want.
    if (isLink) {
      event.preventDefault();
    }
  }
  function handleMouseDown(event) {
    if (isRightClick(event.nativeEvent)) {
      return;
    }
    if (isLink) {
      // Signal that the mouse is down so we can call the right function if the
      // user is clicking on a link.
      mouseEventStarted.current = true;
    } else {
      event.preventDefault();
    }
  }
  function handleMouseEnter(event) {
    var doc = getOwnerDocument(dropdownRef.current);
    if (!isSelected && index != null && !disabled) {
      if (dropdownRef != null && dropdownRef.current && dropdownRef.current !== doc.activeElement && ownRef.current !== doc.activeElement) {
        dropdownRef.current.focus();
      }
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: index
        }
      });
    }
  }
  function handleMouseLeave(event) {
    // Clear out selection when mouse over a non-dropdown-item child.
    dispatch({
      type: CLEAR_SELECTION_INDEX
    });
  }
  function handleMouseMove(event) {
    if (!readyToSelect.current) {
      var threshold = 8;
      var deltaX = Math.abs(event.clientX - mouseDownStartPosRef.current.x);
      var deltaY = Math.abs(event.clientY - mouseDownStartPosRef.current.y);
      if (deltaX > threshold || deltaY > threshold) {
        readyToSelect.current = true;
      }
    }
    if (!isSelected && index != null && !disabled) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: index,
          dropdownRef: dropdownRef
        }
      });
    }
  }
  function handleFocus() {
    readyToSelect.current = true;
    if (!isSelected && index != null && !disabled) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: index
        }
      });
    }
  }
  function handleMouseUp(event) {
    if (isRightClick(event.nativeEvent)) {
      return;
    }
    if (!readyToSelect.current) {
      readyToSelect.current = true;
      return;
    }
    if (isLink) {
      // If a mousedown event was initiated on a link item followed by a mouseup
      // event on the same link, we do nothing; a click event will come next and
      // handle selection. Otherwise, we trigger a click event.
      if (mouseEventStarted.current) {
        mouseEventStarted.current = false;
      } else if (ownRef.current) {
        ownRef.current.click();
      }
    } else {
      if (!disabled) {
        select();
      }
    }
  }
  useEffect(function () {
    if (isExpanded) {
      // When the dropdown opens, wait for about half a second before enabling
      // selection. This is designed to mirror dropdown menus on macOS, where
      // opening a menu on top of a trigger would otherwise result in an
      // immediate accidental selection once the click trigger is released.
      var id = window.setTimeout(function () {
        readyToSelect.current = true;
      }, 400);
      return function () {
        window.clearTimeout(id);
      };
    } else {
      // When the dropdown closes, reset readyToSelect for the next interaction.
      readyToSelect.current = false;
    }
  }, [isExpanded, readyToSelect]); // Any time a mouseup event occurs anywhere in the document, we reset the
  // mouseEventStarted ref so we can check it again when needed.

  useEffect(function () {
    var ownerDocument = getOwnerDocument(ownRef.current);
    ownerDocument.addEventListener("mouseup", listener);
    return function () {
      ownerDocument.removeEventListener("mouseup", listener);
    };
    function listener() {
      mouseEventStarted.current = false;
    }
  }, []);
  return {
    data: {
      disabled: disabled
    },
    props: _extends$1({
      id: useItemId(index),
      tabIndex: -1
    }, props, {
      ref: ref,
      "data-disabled": disabled ? "" : undefined,
      "data-selected": isSelected ? "" : undefined,
      "data-valuetext": valueText,
      onClick: composeEventHandlers(onClick, handleClick),
      onDragStart: composeEventHandlers(onDragStart, handleDragStart),
      onMouseDown: composeEventHandlers(onMouseDown, handleMouseDown),
      onMouseEnter: composeEventHandlers(onMouseEnter, handleMouseEnter),
      onMouseLeave: composeEventHandlers(onMouseLeave, handleMouseLeave),
      onMouseMove: composeEventHandlers(onMouseMove, handleMouseMove),
      onFocus: composeEventHandlers(onFocus, handleFocus),
      onMouseUp: composeEventHandlers(onMouseUp, handleMouseUp)
    })
  };
}
/**
 * DropdownItem
 */

var DropdownItem = /*#__PURE__*/forwardRef(function (_ref5, forwardedRef) {
  var _ref5$as = _ref5.as,
    Comp = _ref5$as === void 0 ? "div" : _ref5$as,
    rest = _objectWithoutPropertiesLoose$1(_ref5, _excluded4$1);
  var _useDropdownItem = useDropdownItem(_extends$1({}, rest, {
      ref: forwardedRef
    })),
    props = _useDropdownItem.props;
  return /*#__PURE__*/createElement(Comp, _extends$1({
    "data-reach-dropdown-item": ""
  }, props));
});
if (process.env.NODE_ENV !== "production") {
  DropdownItem.displayName = "DropdownItem";
} ////////////////////////////////////////////////////////////////////////////////

function useDropdownItems(_ref6) {
  _ref6.id;
  var onKeyDown = _ref6.onKeyDown,
    forwardedRef = _ref6.ref,
    props = _objectWithoutPropertiesLoose$1(_ref6, _excluded5$1);
  var _useDropdownContext3 = useDropdownContext(),
    dispatch = _useDropdownContext3.dispatch,
    triggerRef = _useDropdownContext3.triggerRef,
    dropdownRef = _useDropdownContext3.dropdownRef,
    selectCallbacks = _useDropdownContext3.selectCallbacks,
    dropdownId = _useDropdownContext3.dropdownId,
    _useDropdownContext3$ = _useDropdownContext3.state,
    isExpanded = _useDropdownContext3$.isExpanded,
    triggerId = _useDropdownContext3$.triggerId,
    selectionIndex = _useDropdownContext3$.selectionIndex,
    typeaheadQuery = _useDropdownContext3$.typeaheadQuery;
  var items = useDropdownDescendants();
  var ref = useComposedRefs(dropdownRef, forwardedRef);
  useEffect(function () {
    // Respond to user char key input with typeahead
    var match = findItemFromTypeahead(items, typeaheadQuery);
    if (typeaheadQuery && match != null) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: match,
          dropdownRef: dropdownRef
        }
      });
    }
    var timeout = window.setTimeout(function () {
      return typeaheadQuery && dispatch({
        type: SEARCH_FOR_ITEM,
        payload: ""
      });
    }, 1000);
    return function () {
      return window.clearTimeout(timeout);
    };
  }, [dispatch, items, typeaheadQuery, dropdownRef]);
  var prevItemsLength = usePrevious(items.length);
  var prevSelected = usePrevious(items[selectionIndex]);
  var prevSelectionIndex = usePrevious(selectionIndex);
  useEffect(function () {
    if (selectionIndex > items.length - 1) {
      // If for some reason our selection index is larger than our possible
      // index range (let's say the last item is selected and the list
      // dynamically updates), we need to select the last item in the list.
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: items.length - 1,
          dropdownRef: dropdownRef
        }
      });
    } else if (
    // Checks if
    //  - item length has changed
    //  - selection index has not changed BUT selected item has changed
    //
    // This prevents any dynamic adding/removing of items from actually
    // changing a user's expected selection.
    prevItemsLength !== items.length && selectionIndex > -1 && prevSelected && prevSelectionIndex === selectionIndex && items[selectionIndex] !== prevSelected) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: items.findIndex(function (i) {
            return i.key === (prevSelected == null ? void 0 : prevSelected.key);
          }),
          dropdownRef: dropdownRef
        }
      });
    }
  }, [dropdownRef, dispatch, items, prevItemsLength, prevSelected, prevSelectionIndex, selectionIndex]);
  var handleKeyDown = composeEventHandlers(function handleKeyDown(event) {
    var key = event.key;
    if (!isExpanded) {
      return;
    }
    switch (key) {
      case "Enter":
      case " ":
        var selected = items.find(function (item) {
          return item.index === selectionIndex;
        }); // For links, the Enter key will trigger a click by default, but for
        // consistent behavior across items we'll trigger a click when the
        // spacebar is pressed.

        if (selected && !selected.disabled) {
          event.preventDefault();
          if (selected.isLink && selected.element) {
            selected.element.click();
          } else {
            // Focus the button first by default when an item is selected.
            // We fire the onSelect callback next so the app can manage
            // focus if needed.
            focus(triggerRef.current);
            selectCallbacks.current[selected.index] && selectCallbacks.current[selected.index]();
            dispatch({
              type: CLICK_MENU_ITEM
            });
          }
        }
        break;
      case "Escape":
        focus(triggerRef.current);
        dispatch({
          type: CLOSE_MENU
        });
        break;
      case "Tab":
        // prevent leaving
        event.preventDefault();
        break;
      default:
        // Check if a user is typing some char keys and respond by setting
        // the query state.
        if (isString(key) && key.length === 1) {
          var query = typeaheadQuery + key.toLowerCase();
          dispatch({
            type: SEARCH_FOR_ITEM,
            payload: query
          });
        }
        break;
    }
  }, useDescendantKeyDown(DropdownDescendantContext, {
    currentIndex: selectionIndex,
    orientation: "vertical",
    rotate: false,
    filter: function filter(item) {
      return !item.disabled;
    },
    callback: function callback(index) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: index,
          dropdownRef: dropdownRef
        }
      });
    },
    key: "index"
  }));
  return {
    data: {
      activeDescendant: useItemId(selectionIndex) || undefined,
      triggerId: triggerId
    },
    props: _extends$1({
      tabIndex: -1
    }, props, {
      ref: ref,
      id: dropdownId,
      onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown)
    })
  };
}
/**
 * DropdownItem
 */

var DropdownItems = /*#__PURE__*/forwardRef(function (_ref7, forwardedRef) {
  var _ref7$as = _ref7.as,
    Comp = _ref7$as === void 0 ? "div" : _ref7$as,
    rest = _objectWithoutPropertiesLoose$1(_ref7, _excluded6$1);
  var _useDropdownItems = useDropdownItems(_extends$1({}, rest, {
      ref: forwardedRef
    })),
    props = _useDropdownItems.props;
  return /*#__PURE__*/createElement(Comp, _extends$1({
    "data-reach-dropdown-items": ""
  }, props));
});
if (process.env.NODE_ENV !== "production") {
  DropdownItems.displayName = "DropdownItems";
} ////////////////////////////////////////////////////////////////////////////////

function useDropdownPopover(_ref8) {
  var onBlur = _ref8.onBlur,
    _ref8$portal = _ref8.portal,
    portal = _ref8$portal === void 0 ? true : _ref8$portal,
    position = _ref8.position,
    forwardedRef = _ref8.ref,
    props = _objectWithoutPropertiesLoose$1(_ref8, _excluded7$1);
  var _useDropdownContext4 = useDropdownContext(),
    triggerRef = _useDropdownContext4.triggerRef,
    triggerClickedRef = _useDropdownContext4.triggerClickedRef,
    dispatch = _useDropdownContext4.dispatch,
    dropdownRef = _useDropdownContext4.dropdownRef,
    popoverRef = _useDropdownContext4.popoverRef,
    isExpanded = _useDropdownContext4.state.isExpanded;
  var ref = useComposedRefs(popoverRef, forwardedRef);
  useEffect(function () {
    if (!isExpanded) {
      return;
    }
    var ownerDocument = getOwnerDocument(popoverRef.current);
    function listener(event) {
      if (triggerClickedRef.current) {
        triggerClickedRef.current = false;
      } else if (!popoverContainsEventTarget(popoverRef.current, event.target)) {
        // We on want to close only if focus rests outside the dropdown
        dispatch({
          type: CLOSE_MENU
        });
      }
    }
    ownerDocument.addEventListener("mousedown", listener); // see https://github.com/reach/reach-ui/pull/700#discussion_r530369265
    // ownerDocument.addEventListener("touchstart", listener);

    return function () {
      ownerDocument.removeEventListener("mousedown", listener); // ownerDocument.removeEventListener("touchstart", listener);
    };
  }, [triggerClickedRef, triggerRef, dispatch, dropdownRef, popoverRef, isExpanded]);
  return {
    data: {
      portal: portal,
      position: position,
      targetRef: triggerRef,
      isExpanded: isExpanded
    },
    props: _extends$1({
      ref: ref,
      hidden: !isExpanded,
      onBlur: composeEventHandlers(onBlur, function (event) {
        if (event.currentTarget.contains(event.relatedTarget)) {
          return;
        }
        dispatch({
          type: CLOSE_MENU
        });
      })
    }, props)
  };
}
var DropdownPopover = /*#__PURE__*/forwardRef(function (_ref9, forwardedRef) {
  var _ref9$as = _ref9.as,
    Comp = _ref9$as === void 0 ? "div" : _ref9$as,
    rest = _objectWithoutPropertiesLoose$1(_ref9, _excluded8$1);
  var _useDropdownPopover = useDropdownPopover(_extends$1({}, rest, {
      ref: forwardedRef
    })),
    _useDropdownPopover$d = _useDropdownPopover.data,
    portal = _useDropdownPopover$d.portal,
    targetRef = _useDropdownPopover$d.targetRef,
    position = _useDropdownPopover$d.position,
    props = _useDropdownPopover.props;
  var sharedProps = {
    "data-reach-dropdown-popover": ""
  };
  return portal ? /*#__PURE__*/createElement(Popover, _extends$1({}, props, sharedProps, {
    as: Comp,
    targetRef: targetRef,
    position: position
  })) : /*#__PURE__*/createElement(Comp, _extends$1({}, props, sharedProps));
});
if (process.env.NODE_ENV !== "production") {
  DropdownPopover.displayName = "DropdownPopover";
} ////////////////////////////////////////////////////////////////////////////////

/**
 * When a user's typed input matches the string displayed in an item, it is
 * expected that the matching item is selected. This is our matching function.
 */

function findItemFromTypeahead(items, string) {
  if (string === void 0) {
    string = "";
  }
  if (!string) {
    return null;
  }
  var found = items.find(function (item) {
    var _item$element, _item$element$dataset, _item$element$dataset2;
    return item.disabled ? false : (_item$element = item.element) == null ? void 0 : (_item$element$dataset = _item$element.dataset) == null ? void 0 : (_item$element$dataset2 = _item$element$dataset.valuetext) == null ? void 0 : _item$element$dataset2.toLowerCase().startsWith(string);
  });
  return found ? items.indexOf(found) : null;
}
function useItemId(index) {
  var _React$useContext = useContext(DropdownContext),
    dropdownId = _React$useContext.dropdownId;
  return index != null && index > -1 ? makeId("option-" + index, dropdownId) : undefined;
}
function focus(element) {
  element && element.focus();
}
function popoverContainsEventTarget(popover, target) {
  return !!(popover && popover.contains(target));
}
function reducer(state, action) {
  if (action === void 0) {
    action = {};
  }
  switch (action.type) {
    case CLICK_MENU_ITEM:
      return _extends$1({}, state, {
        isExpanded: false,
        selectionIndex: -1
      });
    case CLOSE_MENU:
      return _extends$1({}, state, {
        isExpanded: false,
        selectionIndex: -1
      });
    case OPEN_MENU_AT_FIRST_ITEM:
      return _extends$1({}, state, {
        isExpanded: true,
        selectionIndex: 0
      });
    case OPEN_MENU_AT_INDEX:
      return _extends$1({}, state, {
        isExpanded: true,
        selectionIndex: action.payload.index
      });
    case OPEN_MENU_CLEARED:
      return _extends$1({}, state, {
        isExpanded: true,
        selectionIndex: -1
      });
    case SELECT_ITEM_AT_INDEX:
      {
        var _action$payload$dropd = action.payload.dropdownRef,
          dropdownRef = _action$payload$dropd === void 0 ? {
            current: null
          } : _action$payload$dropd;
        if (action.payload.index >= 0 && action.payload.index !== state.selectionIndex) {
          if (dropdownRef.current) {
            var doc = getOwnerDocument(dropdownRef.current);
            if (dropdownRef.current !== (doc == null ? void 0 : doc.activeElement)) {
              dropdownRef.current.focus();
            }
          }
          return _extends$1({}, state, {
            selectionIndex: action.payload.max != null ? Math.min(Math.max(action.payload.index, 0), action.payload.max) : Math.max(action.payload.index, 0)
          });
        }
        return state;
      }
    case CLEAR_SELECTION_INDEX:
      return _extends$1({}, state, {
        selectionIndex: -1
      });
    case SET_BUTTON_ID:
      return _extends$1({}, state, {
        triggerId: action.payload
      });
    case SEARCH_FOR_ITEM:
      if (typeof action.payload !== "undefined") {
        return _extends$1({}, state, {
          typeaheadQuery: action.payload
        });
      }
      return state;
    default:
      return state;
  }
}
function useDropdownContext() {
  return useContext(DropdownContext);
}
function useDropdownDescendants() {
  return useDescendants(DropdownDescendantContext);
} ////////////////////////////////////////////////////////////////////////////////

/* eslint-disable react-hooks/rules-of-hooks */
var checkedPkgs = {};
/**
 * When in dev mode, checks that styles for a given `@reach` package are loaded.
 *
 * @param packageName Name of the package to check.
 * @example checkStyles("dialog") will check for styles for @reach/dialog
 */

function checkStyles(packageName) {
  if (process.env.NODE_ENV !== "production") {
    // In CJS files, process.env.NODE_ENV is stripped from our build, but we
    // need it to prevent style checks from clogging up user logs while testing.
    // This is a workaround until we can tweak the build a bit to accommodate.
    var _ref = typeof process !== "undefined" ? process.env : {
        NODE_ENV: "development"
      },
      environment = _ref.NODE_ENV; // only check once per package

    if (checkedPkgs[packageName]) return;
    checkedPkgs[packageName] = true;
    if (environment === "development" && parseInt(window.getComputedStyle(document.body).getPropertyValue("--reach-" + packageName), 10) !== 1) {
      console.warn("@reach/" + packageName + " styles not found. If you are using a bundler like webpack or parcel include this in the entry file of your app before any of your own styles:\n  \n      import \"@reach/" + packageName + "/styles.css\";\n  \n    Otherwise you'll need to include them some other way:\n  \n      <link rel=\"stylesheet\" type=\"text/css\" href=\"node_modules/@reach/" + packageName + "/styles.css\" />\n  \n    For more information visit https://ui.reach.tech/styling.\n    ");
    }
  }
}
/**
 * When in dev mode, checks that styles for a given `@reach` package are loaded.
 *
 * @param packageName Name of the package to check.
 * @example useCheckStyles("dialog") will check for styles for @reach/dialog
 */

function useCheckStyles(packageName) {
  if (process.env.NODE_ENV !== "production") {
    var name = useRef(packageName);
    useEffect(function () {
      return void (name.current = packageName);
    }, [packageName]);
    useEffect(function () {
      return checkStyles(name.current);
    }, []);
  }
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var _excluded = ["as", "id", "children"],
  _excluded2 = ["as"],
  _excluded3 = ["as"],
  _excluded4 = ["as"],
  _excluded5 = ["as"],
  _excluded6 = ["as", "component", "onSelect"],
  _excluded7 = ["portal"],
  _excluded8 = ["as"];
////////////////////////////////////////////////////////////////////////////////

/**
 * Menu
 *
 * The wrapper component for the other components. No DOM element is rendered.
 *
 * @see Docs https://reach.tech/menu-button#menu
 */
var Menu = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var _ref$as = _ref.as,
    Comp = _ref$as === void 0 ? Fragment : _ref$as,
    id = _ref.id,
    children = _ref.children,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  useCheckStyles("menu-button");
  var parentIsFragment = useMemo(function () {
    try {
      // To test if the component renders a fragment we need to actually
      // render it, but this may throw an error since we can't predict what is
      // actually provided. There's technically a small chance that this could
      // get it wrong but I don't think it's too likely in practice.
      return reactIs.isFragment(/*#__PURE__*/createElement(Comp, null));
    } catch (err) {
      return false;
    }
  }, [Comp]);
  var props = parentIsFragment ? {} : _extends({
    ref: forwardedRef,
    id: id,
    "data-reach-menu": ""
  }, rest);
  return /*#__PURE__*/createElement(Comp, props, /*#__PURE__*/createElement(DropdownProvider, {
    id: id,
    children: children
  }));
});
/**
 * @see Docs https://reach.tech/menu-button#menu-props
 */

if (process.env.NODE_ENV !== "production") {
  Menu.displayName = "Menu";
  Menu.propTypes = {
    children: /*#__PURE__*/PropTypes.oneOfType([PropTypes.func, PropTypes.node])
  };
} ////////////////////////////////////////////////////////////////////////////////

/**
 * MenuButton
 *
 * Wraps a DOM `button` that toggles the opening and closing of the dropdown
 * menu. Must be rendered inside of a `<Menu>`.
 *
 * @see Docs https://reach.tech/menu-button#menubutton
 */

var MenuButton = /*#__PURE__*/forwardRef(function (_ref2, forwardedRef) {
  var _ref2$as = _ref2.as,
    Comp = _ref2$as === void 0 ? "button" : _ref2$as,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  var _useDropdownTrigger = useDropdownTrigger(_extends({}, rest, {
      ref: forwardedRef
    })),
    _useDropdownTrigger$d = _useDropdownTrigger.data,
    isExpanded = _useDropdownTrigger$d.isExpanded,
    controls = _useDropdownTrigger$d.controls,
    props = _useDropdownTrigger.props;
  return /*#__PURE__*/createElement(Comp // When the menu is displayed, the element with role `button` has
  // `aria-expanded` set to `true`. When the menu is hidden, it is
  // recommended that `aria-expanded` is not present.
  // https://www.w3.org/TR/wai-aria-practices-1.2/#menubutton
  , _extends({
    "aria-expanded": isExpanded ? true : undefined // The element with role `button` has `aria-haspopup` set to either
    // `"menu"` or `true`.
    // https://www.w3.org/TR/wai-aria-practices-1.2/#menubutton
    ,

    "aria-haspopup": true // Optionally, the element with role `button` has a value specified for
    // `aria-controls` that refers to the element with role `menu`.
    // https://www.w3.org/TR/wai-aria-practices-1.2/#menubutton
    ,

    "aria-controls": controls
  }, props, {
    "data-reach-menu-button": ""
  }));
});
/**
 * @see Docs https://reach.tech/menu-button#menubutton-props
 */

if (process.env.NODE_ENV !== "production") {
  MenuButton.displayName = "MenuButton";
  MenuButton.propTypes = {
    children: PropTypes.node
  };
} ////////////////////////////////////////////////////////////////////////////////

/**
 * MenuItemImpl
 *
 * MenuItem and MenuLink share most of the same functionality captured here.
 */

var MenuItemImpl = /*#__PURE__*/forwardRef(function (_ref3, forwardedRef) {
  var _ref3$as = _ref3.as,
    Comp = _ref3$as === void 0 ? "div" : _ref3$as,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  var _useDropdownItem = useDropdownItem(_extends({}, rest, {
      ref: forwardedRef
    })),
    disabled = _useDropdownItem.data.disabled,
    props = _useDropdownItem.props;
  return /*#__PURE__*/createElement(Comp, _extends({
    role: "menuitem"
  }, props, {
    "aria-disabled": disabled || undefined,
    "data-reach-menu-item": ""
  }));
});
////////////////////////////////////////////////////////////////////////////////

/**
 * MenuItem
 *
 * Handles menu selection. Must be a direct child of a `<MenuList>`.
 *
 * @see Docs https://reach.tech/menu-button#menuitem
 */
var MenuItem = /*#__PURE__*/forwardRef(function (_ref4, forwardedRef) {
  var _ref4$as = _ref4.as,
    as = _ref4$as === void 0 ? "div" : _ref4$as,
    props = _objectWithoutPropertiesLoose(_ref4, _excluded4);
  return /*#__PURE__*/createElement(MenuItemImpl, _extends({}, props, {
    ref: forwardedRef,
    as: as
  }));
});
/**
 * @see Docs https://reach.tech/menu-button#menuitem-props
 */

if (process.env.NODE_ENV !== "production") {
  MenuItem.displayName = "MenuItem";
  MenuItem.propTypes = {
    as: PropTypes.any,
    onSelect: PropTypes.func.isRequired
  };
} ////////////////////////////////////////////////////////////////////////////////

/**
 * MenuItems
 *
 * A low-level wrapper for menu items. Compose it with `MenuPopover` for more
 * control over the nested components and their rendered DOM nodes, or if you
 * need to nest arbitrary components between the outer wrapper and your list.
 *
 * @see Docs https://reach.tech/menu-button#menuitems
 */

var MenuItems = /*#__PURE__*/forwardRef(function (_ref5, forwardedRef) {
  var _ref5$as = _ref5.as,
    Comp = _ref5$as === void 0 ? "div" : _ref5$as,
    rest = _objectWithoutPropertiesLoose(_ref5, _excluded5);
  var _useDropdownItems = useDropdownItems(_extends({}, rest, {
      ref: forwardedRef
    })),
    _useDropdownItems$dat = _useDropdownItems.data,
    activeDescendant = _useDropdownItems$dat.activeDescendant,
    triggerId = _useDropdownItems$dat.triggerId,
    props = _useDropdownItems.props;
  return (/*#__PURE__*/
    // TODO: Should probably file a but in jsx-a11y, but this is correct
    // according to https://www.w3.org/TR/wai-aria-practices-1.2/examples/menu-button/menu-button-actions-active-descendant.html
    // eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex
    createElement(Comp // Refers to the descendant menuitem element that is visually indicated
    // as focused.
    // https://www.w3.org/TR/wai-aria-practices-1.2/examples/menu-button/menu-button-actions-active-descendant.html
    , _extends({
      "aria-activedescendant": activeDescendant // Refers to the element that contains the accessible name for the
      // `menu`. The menu is labeled by the menu button.
      // https://www.w3.org/TR/wai-aria-practices-1.2/examples/menu-button/menu-button-actions-active-descendant.html
      ,

      "aria-labelledby": triggerId || undefined // The element that contains the menu items displayed by activating the
      // button has role menu.
      // https://www.w3.org/TR/wai-aria-practices-1.2/#menubutton
      ,

      role: "menu"
    }, props, {
      "data-reach-menu-items": ""
    }))
  );
});
/**
 * @see Docs https://reach.tech/menu-button#menuitems-props
 */

if (process.env.NODE_ENV !== "production") {
  MenuItems.displayName = "MenuItems";
  MenuItems.propTypes = {
    children: PropTypes.node
  };
} ////////////////////////////////////////////////////////////////////////////////

/**
 * MenuLink
 *
 * Handles linking to a different page in the menu. By default it renders `<a>`,
 * but also accepts any other kind of Link as long as the `Link` uses the
 * `React.forwardRef` API.
 *
 * Must be a direct child of a `<MenuList>`.
 *
 * @see Docs https://reach.tech/menu-button#menulink
 */

var MenuLink = /*#__PURE__*/forwardRef(function (_ref6, forwardedRef) {
  var _ref6$as = _ref6.as,
    as = _ref6$as === void 0 ? "a" : _ref6$as,
    component = _ref6.component,
    onSelect = _ref6.onSelect,
    props = _objectWithoutPropertiesLoose(_ref6, _excluded6);
  useDevWarning(!component, "[@reach/menu-button]: Please use the `as` prop instead of `component`");
  return /*#__PURE__*/createElement(MenuItemImpl, _extends({}, props, {
    ref: forwardedRef,
    "data-reach-menu-link": "",
    as: as,
    isLink: true,
    onSelect: onSelect || noop
  }));
});
/**
 * @see Docs https://reach.tech/menu-button#menulink-props
 */

if (process.env.NODE_ENV !== "production") {
  MenuLink.displayName = "MenuLink";
  MenuLink.propTypes = {
    as: PropTypes.any
  };
} ////////////////////////////////////////////////////////////////////////////////

/**
 * MenuList
 *
 * Wraps a DOM element that renders the menu items. Must be rendered inside of
 * a `<Menu>`.
 *
 * @see Docs https://reach.tech/menu-button#menulist
 */

var MenuList = /*#__PURE__*/forwardRef(function (_ref7, forwardedRef) {
  var _ref7$portal = _ref7.portal,
    portal = _ref7$portal === void 0 ? true : _ref7$portal,
    props = _objectWithoutPropertiesLoose(_ref7, _excluded7);
  return /*#__PURE__*/createElement(MenuPopover, {
    portal: portal
  }, /*#__PURE__*/createElement(MenuItems, _extends({}, props, {
    ref: forwardedRef,
    "data-reach-menu-list": ""
  })));
});
/**
 * @see Docs https://reach.tech/menu-button#menulist-props
 */

if (process.env.NODE_ENV !== "production") {
  MenuList.displayName = "MenuList";
  MenuList.propTypes = {
    children: PropTypes.node.isRequired
  };
} ////////////////////////////////////////////////////////////////////////////////

/**
 * MenuPopover
 *
 * A low-level wrapper for the popover that appears when a menu button is open.
 * You can compose it with `MenuItems` for more control over the nested
 * components and their rendered DOM nodes, or if you need to nest arbitrary
 * components between the outer wrapper and your list.
 *
 * @see Docs https://reach.tech/menu-button#menupopover
 */

var MenuPopover = /*#__PURE__*/forwardRef(function (_ref8, forwardedRef) {
  var _ref8$as = _ref8.as,
    Comp = _ref8$as === void 0 ? "div" : _ref8$as,
    rest = _objectWithoutPropertiesLoose(_ref8, _excluded8);
  var _useDropdownPopover = useDropdownPopover(_extends({}, rest, {
      ref: forwardedRef
    })),
    _useDropdownPopover$d = _useDropdownPopover.data,
    portal = _useDropdownPopover$d.portal,
    targetRef = _useDropdownPopover$d.targetRef,
    position = _useDropdownPopover$d.position,
    props = _useDropdownPopover.props;
  var sharedProps = {
    "data-reach-menu-popover": ""
  };
  return portal ? /*#__PURE__*/createElement(Popover, _extends({}, props, sharedProps, {
    as: Comp,
    targetRef: targetRef,
    position: position
  })) : /*#__PURE__*/createElement(Comp, _extends({}, props, sharedProps));
});
/**
 * @see Docs https://reach.tech/menu-button#menupopover-props
 */

if (process.env.NODE_ENV !== "production") {
  MenuPopover.displayName = "MenuPopover";
  MenuPopover.propTypes = {
    children: PropTypes.node
  };
} ////////////////////////////////////////////////////////////////////////////////

/**
 * A hook that exposes data for a given `Menu` component to its descendants.
 *
 * @see Docs https://reach.tech/menu-button#usemenubuttoncontext
 */

function useMenuButtonContext() {
  var _useDropdownContext = useDropdownContext(),
    isExpanded = _useDropdownContext.state.isExpanded;
  return useMemo(function () {
    return {
      isExpanded: isExpanded
    };
  }, [isExpanded]);
} ////////////////////////////////////////////////////////////////////////////////

function useDevWarning(condition, message) {
  if (process.env.NODE_ENV !== "production") {
    /* eslint-disable react-hooks/rules-of-hooks */
    var messageRef = useRef(message);
    useEffect(function () {
      messageRef.current = message;
    }, [message]);
    useEffect(function () {
      process.env.NODE_ENV !== "production" ? warning(condition, messageRef.current) : void 0;
    }, [condition]);
    /* eslint-enable react-hooks/rules-of-hooks */
  }
} ////////////////////////////////////////////////////////////////////////////////

export { Menu as M, MenuButton as a, MenuItem as b, MenuItems as c, MenuLink as d, MenuPopover as e, useMenuButtonContext as u };
